import { _, actions, api, config, helpers, React, ts, useDispatch, useLocation, useSelector } from '_core';

const useWorkspaceMetadataBackgroundSave = () => {
  const { pathname } = useLocation();

  const dispatch = useDispatch();
  const currentWorkspaceId = useSelector((state) => state.ui.currentWorkspaceId);
  const currentTab = useSelector((state) => state.ui.currentTab);

  const updateWorkspacesMetadata = (w: ts.types.workspace.WorkspacesMetadata) =>
    dispatch(actions.ui.updateWorkspacesMetadata(w));

  const [itemType, setItemType] = React.useState<ts.enums.RESOURCES_TYPES_ENUM>();
  const [itemId, setItemId] = React.useState<number>();

  React.useEffect(() => {
    const split = pathname.split('/');
    const localItemType = split?.[1] ? (helpers.pluralize.singular(split[1]) as ts.enums.RESOURCES_TYPES_ENUM) : null;
    const localItemId = split?.[2] ? parseInt(split?.[2]) * 1 : null;

    if (localItemType && Object.values(ts.enums.RESOURCES_TYPES_ENUM).includes(localItemType)) {
      setItemType(localItemType);
      setItemId(localItemId);
    } else {
      setItemType(null);
      setItemId(null);
    }
  }, [pathname]);

  // Gets workspace context based on resource type first, then fall back to currentTab
  // This prevents race conditions where currentTab hasn't updated yet
  const getWorkspaceContext = React.useCallback(
    (type: ts.enums.RESOURCES_TYPES_ENUM | undefined): ts.types.workspace.Workspace['context'] => {
      if (!type) return currentTab === 'production' ? 'production' : 'research';
      // Production resources are always production context
      if (
        config.productionResources.includes(
          // We know that there is an overlap between RESOURCES_TYPES_ENUM and UI_RESOURCE_TYPE_ENUM
          // so we can cast it to UI_RESOURCE_TYPE_ENUM
          type as unknown as ts.enums.UI_RESOURCE_TYPE_ENUM
        )
      ) {
        return 'production';
      }
      // Otherwise use currentTab
      return currentTab === 'production' ? 'production' : 'research';
    },
    [currentTab]
  );

  const getRecent10 = (
    lastItem: ts.types.workspace.WorkspaceMetadataItem,
    wsrecent: ts.types.workspace.WorkspaceMetadataItem[]
  ): ts.types.workspace.WorkspaceMetadataItem[] => {
    let recent = wsrecent || [];
    // add data, slice to stack size and get uniq
    recent = _.uniqWith([lastItem, ...recent].slice(0, 10), _.isEqual) as ts.types.workspace.WorkspaceMetadataItem[];
    return recent;
  };

  const debouncedSave = React.useMemo(
    () =>
      _.debounce(
        async (
          itemId: number,
          itemType: ts.enums.RESOURCES_TYPES_ENUM,
          currentWorkspaceId: number,
          wsContext: ts.types.workspace.Workspace['context']
        ) => {
          const buildNewData = (
            currentWorkspaceId: number,
            workspaceMetadata: ts.types.workspace.WorkspacesMetadata
          ) => {
            if (wsContext == 'production') {
              const newData = { prodcurrentWorkspaceId: currentWorkspaceId } as ts.types.workspace.WorkspacesMetadata;
              if (itemType && itemId) {
                newData.prodlast = { itemId, itemType };
                newData.prodrecent = getRecent10({ itemId, itemType }, workspaceMetadata.prodrecent);
              }
              return newData;
            }

            const newData = { currentWorkspaceId } as ts.types.workspace.WorkspacesMetadata;
            if (itemType && itemId) {
              newData.last = { itemId, itemType };
              newData.recent = getRecent10({ itemId, itemType }, workspaceMetadata.recent);
            }
            return newData;
          };

          try {
            const workspaceMetadata = (await api.users.getMe()).workspaces_metadata;
            const newData = buildNewData(currentWorkspaceId, workspaceMetadata);
            const updatedMetadata = { ...workspaceMetadata, ...newData };
            // store in redux
            updateWorkspacesMetadata({ ...updatedMetadata });
            // call to api
            await api.users.updateMe({ workspaces_metadata: updatedMetadata });
          } catch (e) {
            console.log('unable to store last state of workspaces', e);
          }
        },
        5000
      ),
    []
  );

  const authenticated = useSelector((state) => state.auth.authenticated);

  // Update the current resource on workspace
  React.useEffect(() => {
    if (!authenticated) return;
    if (!itemType || !itemId) return;

    const wsContext = getWorkspaceContext(itemType);
    if (wsContext === 'production' || wsContext === 'research') {
      debouncedSave(itemId, itemType, currentWorkspaceId, wsContext);
    }
  }, [itemType, itemId, currentWorkspaceId, getWorkspaceContext, debouncedSave, authenticated]);
};

export default useWorkspaceMetadataBackgroundSave;
